/**
 * Composable for handling asset loading in Vue components
 */
import { ref, nextTick } from 'vue';
// import { ref, onMounted, nextTick } from 'vue';
import { waitForAssetsToLoad } from '@/utils/waitForAssets';

/** Delay (ms) between assets finishing load and the template fading in. */
const FADE_IN_DELAY_MS = 500;

export function useAssetLoading(maxWaitTime: number = 3000) {
  const isAssetsLoaded = ref(false);
  const isAssetsError = ref(false);
  const isTemplateReadyToFadeIn = ref(false);

  const waitForComponentAssets = async (containerRef?: Element) => {
    try {
      // // Wait for Vue to finish rendering
      await nextTick();

      // // Wait a small amount for any dynamic content to be inserted
      await new Promise((resolve) => setTimeout(resolve, 100));

      // // Now wait for assets in the component
      const targetElement = containerRef || document.body;
      await waitForAssetsToLoad(maxWaitTime, targetElement);

      isAssetsLoaded.value = true;
    } catch (error) {
      console.warn('Asset loading failed:', error);
      isAssetsError.value = true;
      // // Still show content even if assets fail
      isAssetsLoaded.value = true;
    }
  };

  /**
   * Call once assets are loaded (e.g. right after `waitForComponentAssets`
   * resolves) to flip `isTemplateReadyToFadeIn` after a short delay — this
   * gives the fade-in transition a beat to actually be visible instead of
   * snapping in the instant assets resolve.
   */
  const templateFadeInHandler = () => {
    setTimeout(() => {
      isTemplateReadyToFadeIn.value = true;
    }, FADE_IN_DELAY_MS);
  };

  return {
    isAssetsLoaded,
    isAssetsError,
    isTemplateReadyToFadeIn,
    waitForComponentAssets,
    templateFadeInHandler,
  };
}
